import { useState } from 'react';
import {
  ArrowLeft,
  LayoutDashboard,
  BarChart3,
  Grid3X3,
  Filter,
  MousePointer2,
  Trophy,
  Route,
  Target,
  Lightbulb,
  Settings,
  Building2,
  Users,
  RefreshCw,
  Tags,
  Link,
  ChevronDown,
  ChevronRight,
  Search,
  HelpCircle,
  BookOpen,
  PlayCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  ArrowRight,
  Calendar,
  Download,
  Eye,
  Edit3,
  Trash2,
  Plus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

export default function Help() {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState<string[]>(['getting-started']);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSection = (id: string) => {
    setExpandedSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const expandAll = () => {
    setExpandedSections(sections.map((s) => s.id));
  };

  const collapseAll = () => {
    setExpandedSections([]);
  };

  const sections: Section[] = [
    {
      id: 'getting-started',
      title: 'はじめに - majirakunとは？',
      icon: <BookOpen className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <h3 className="text-xl font-bold text-blue-900 mb-4">majirakunへようこそ！</h3>
            <p className="text-blue-800 text-lg leading-relaxed mb-4">
              <strong>majiraku</strong>は、UTAGEというマーケティングツールのデータを
              <span className="bg-yellow-200 px-1">自動で毎日集めて</span>、
              わかりやすい<span className="bg-yellow-200 px-1">グラフや表</span>で
              見せてくれるシステムです。
            </p>
            <div className="bg-white/70 rounded-lg p-4 mt-4">
              <p className="text-blue-700 text-sm">
                <strong>なぜこのシステムが必要？</strong><br />
                UTAGEの管理画面では、データが散らばっていて全体像が見えにくいです。
                majirakunを使えば、<strong>複数のアカウントのデータを一元管理</strong>し、
                <strong>グラフで視覚的に</strong>理解でき、<strong>AIが自動で分析</strong>してくれます。
                手作業でExcelにまとめる必要がなくなり、時間を大幅に節約できます。
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-5">
              <h4 className="font-bold text-green-800 mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                このシステムでできること
              </h4>
              <ul className="space-y-2 text-green-700">
                <li className="flex items-start">
                  <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">1</span>
                  <span>LINEやメールの<strong>登録者数</strong>を毎日自動で集計</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">2</span>
                  <span><strong>クリック率</strong>や<strong>反応率</strong>をグラフで一目で確認</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">3</span>
                  <span>どこから人が来ているか（<strong>流入経路</strong>）を分析</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">4</span>
                  <span><strong>AI</strong>が自動でデータを分析してアドバイス</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">5</span>
                  <span>データを<strong>Excel/CSV</strong>でダウンロード</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">6</span>
                  <span><strong>ファネル分析</strong>で離脱ポイントを特定</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5">7</span>
                  <span><strong>コンバージョン率</strong>を自動計算</span>
                </li>
              </ul>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-5">
              <h4 className="font-bold text-orange-800 mb-3 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                注意点
              </h4>
              <ul className="space-y-2 text-orange-700">
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">・</span>
                  <span>UTAGEの設定が正しくないとデータが取れません</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">・</span>
                  <span>データは毎日深夜に自動更新されます</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">・</span>
                  <span>過去のデータは削除すると元に戻せません</span>
                </li>
              </ul>
              <div className="mt-4 bg-white/70 rounded p-3">
                <p className="text-orange-700 text-sm">
                  <strong>データ取得の仕組み：</strong><br />
                  毎日深夜にシステムが自動でUTAGEにログインし、統計データを取得します。
                  そのため、本日のデータは翌日以降に反映されます。
                </p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-5">
            <h4 className="font-bold text-yellow-800 mb-4 text-lg">覚えておきたい用語（初心者向け）</h4>
            <p className="text-yellow-700 text-sm mb-4">
              以下の用語を理解しておくと、システムの操作がスムーズになります。わからない時はここに戻って確認してください。
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <dt className="font-bold text-yellow-900 text-lg">ラベル（タグ）</dt>
                <dd className="text-yellow-700 mt-1">
                  <strong>お客さんに付けるしるし</strong>のこと。「この人はLINE登録した」「この人はリンクをクリックした」など、行動を記録するために使います。<br />
                  <span className="text-sm bg-gray-100 px-2 py-1 rounded mt-2 inline-block">
                    例：「LINE登録済み」「セミナー申込」「タップ済み」
                  </span>
                  <p className="text-xs text-gray-500 mt-2">
                    ※ UTAGEでは「ラベル」、他のツールでは「タグ」と呼ぶこともあります
                  </p>
                </dd>
              </div>
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <dt className="font-bold text-yellow-900 text-lg">アカウント</dt>
                <dd className="text-yellow-700 mt-1">
                  <strong>LINE公式アカウントやメールマガジン</strong>のこと。1つの会社で複数のアカウントを持っている場合があります。<br />
                  <span className="text-sm bg-gray-100 px-2 py-1 rounded mt-2 inline-block">
                    例：「公式LINE」「メルマガ」「サブLINE」
                  </span>
                  <p className="text-xs text-gray-500 mt-2">
                    ※ アカウントごとにデータを分けて見ることができます
                  </p>
                </dd>
              </div>
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <dt className="font-bold text-yellow-900 text-lg">法人（会社）</dt>
                <dd className="text-yellow-700 mt-1">
                  <strong>会社やお店</strong>のこと。majirakunでは、法人ごとにデータを管理します。<br />
                  <p className="text-xs text-gray-500 mt-2">
                    ※ 1つの法人に複数のアカウント（LINE、メール等）が紐づいています
                  </p>
                </dd>
              </div>
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <dt className="font-bold text-yellow-900 text-lg">シナリオ</dt>
                <dd className="text-yellow-700 mt-1">
                  UTAGEで設定した<strong>メッセージ配信の流れ</strong>。お客さんがどの入口から入ってきたかを示します。<br />
                  <span className="text-sm bg-gray-100 px-2 py-1 rounded mt-2 inline-block">
                    例：「オプトイン」「個別案内」「ステップ配信」
                  </span>
                  <p className="text-xs text-gray-500 mt-2">
                    ※ シナリオ = 流入経路と考えるとわかりやすい
                  </p>
                </dd>
              </div>
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <dt className="font-bold text-yellow-900 text-lg">日次値</dt>
                <dd className="text-yellow-700 mt-1">
                  <strong>その日だけの数値</strong>。例えば「今日5人登録した」という数字。
                  <p className="text-xs text-gray-500 mt-2">
                    ※ 日々の変化を見るときに使います
                  </p>
                </dd>
              </div>
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <dt className="font-bold text-yellow-900 text-lg">累計値</dt>
                <dd className="text-yellow-700 mt-1">
                  <strong>これまでの合計</strong>。例えば「累計で100人登録している」という数字。
                  <p className="text-xs text-gray-500 mt-2">
                    ※ 全体の規模を見るときに使います
                  </p>
                </dd>
              </div>
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <dt className="font-bold text-yellow-900 text-lg">CVR（コンバージョン率）</dt>
                <dd className="text-yellow-700 mt-1">
                  <strong>目標達成率</strong>のこと。例えば「登録した人のうち何%が購入したか」を示します。
                  <p className="text-xs text-gray-500 mt-2">
                    ※ CVRが高い = 効率が良い
                  </p>
                </dd>
              </div>
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <dt className="font-bold text-yellow-900 text-lg">ファネル</dt>
                <dd className="text-yellow-700 mt-1">
                  お客さんが<strong>最終目標に至るまでの流れ</strong>。「登録→クリック→予約→購入」のように段階的に人数が減っていく様子を表します。
                  <p className="text-xs text-gray-500 mt-2">
                    ※ 漏斗（じょうご）の形に似ているのでファネルと呼びます
                  </p>
                </dd>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <h4 className="font-bold text-blue-800 mb-3 flex items-center">
              <PlayCircle className="w-5 h-5 mr-2" />
              最初にやること（3ステップ）
            </h4>
            <div className="space-y-3">
              <div className="flex items-center bg-white rounded-lg p-3 border border-blue-100">
                <span className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">1</span>
                <div>
                  <p className="font-medium text-blue-900">ログインする</p>
                  <p className="text-sm text-blue-600">メールアドレスとパスワードを入力してログイン画面から入ります</p>
                </div>
              </div>
              <div className="flex items-center bg-white rounded-lg p-3 border border-blue-100">
                <span className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">2</span>
                <div>
                  <p className="font-medium text-blue-900">法人を選ぶ</p>
                  <p className="text-sm text-blue-600">画面上部のドロップダウンから見たい会社を選択します</p>
                </div>
              </div>
              <div className="flex items-center bg-white rounded-lg p-3 border border-blue-100">
                <span className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3">3</span>
                <div>
                  <p className="font-medium text-blue-900">期間を選ぶ</p>
                  <p className="text-sm text-blue-600">見たい期間（開始日〜終了日）をカレンダーから選択します</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5">
            <h4 className="font-bold text-indigo-800 mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2" />
              このシステムを使うと何がわかる？
            </h4>
            <div className="space-y-3 text-indigo-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">集客の状況</p>
                <p className="text-sm">毎日何人がLINE登録やメール登録しているかがグラフで一目でわかります</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">反応率の把握</p>
                <p className="text-sm">送ったメッセージをどれくらいの人がクリックしているかがわかります</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">離脱ポイントの特定</p>
                <p className="text-sm">「登録→クリック→予約」の流れで、どこで人が減っているかがわかります</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">効果的な施策の発見</p>
                <p className="text-sm">どのシナリオ、どのリンクが一番効果があるかがわかります</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <h4 className="font-bold text-green-800 mb-3 flex items-center">
              <Target className="w-5 h-5 mr-2" />
              分析結果を何に活かせる？
            </h4>
            <div className="space-y-3 text-green-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">マーケティング改善</p>
                <p className="text-sm">クリック率が低いリンクのタイトルや説明文を改善できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">予算配分の最適化</p>
                <p className="text-sm">効果の高い流入経路に広告費を集中させられます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">売上予測</p>
                <p className="text-sm">過去のCVRから、今後の売上を予測できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">報告書作成</p>
                <p className="text-sm">データをExcelでダウンロードして、クライアントへの報告書に活用できます</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'screen-layout',
      title: '画面の見方（基本レイアウト）',
      icon: <Eye className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <p className="text-gray-700 text-lg">
            majirakunの画面は、どのページでも<strong>同じレイアウト</strong>になっています。
            一度覚えれば、すべてのページで迷わず操作できます。
          </p>

          <div className="bg-gray-100 rounded-xl p-6 border-2 border-gray-300">
            <div className="space-y-4">
              {/* ヘッダー */}
              <div className="bg-white rounded-lg p-4 border-2 border-blue-400 relative">
                <span className="absolute -top-3 -left-3 bg-blue-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold text-sm">1</span>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="font-bold text-primary">majiraku</span>
                    <span className="text-sm text-gray-500">UTAGE分析ダッシュボード</span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="bg-gray-100 px-2 py-1 rounded">ユーザー名</span>
                    <span className="text-blue-500 flex items-center"><HelpCircle className="w-4 h-4 mr-1" />ヘルプ</span>
                    <span className="text-gray-500">ログアウト</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                {/* サイドバー */}
                <div className="w-48 bg-white rounded-lg p-4 border-2 border-green-400 relative">
                  <span className="absolute -top-3 -left-3 bg-green-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold text-sm">2</span>
                  <div className="space-y-2 text-sm">
                    <div className="bg-primary text-white px-3 py-2 rounded">ダッシュボード</div>
                    <div className="px-3 py-2 text-gray-600">データ分析</div>
                    <div className="px-3 py-2 text-gray-600">クロス分析</div>
                    <div className="px-3 py-2 text-gray-600">ファネル分析</div>
                    <div className="px-3 py-2 text-gray-600">...</div>
                  </div>
                </div>

                {/* メインコンテンツ */}
                <div className="flex-1 bg-white rounded-lg p-4 border-2 border-purple-400 relative min-h-[200px]">
                  <span className="absolute -top-3 -left-3 bg-purple-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold text-sm">3</span>
                  <div className="text-center text-gray-400 mt-16">
                    メインコンテンツエリア<br />
                    （ここにグラフや表が表示されます）
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <span className="bg-blue-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold text-sm mr-2">1</span>
                <h4 className="font-bold text-blue-800">ヘッダー</h4>
              </div>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>・ロゴとシステム名</li>
                <li>・ログイン中のユーザー名</li>
                <li>・<strong>ヘルプボタン</strong>（このページ）</li>
                <li>・ログアウトボタン</li>
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <span className="bg-green-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold text-sm mr-2">2</span>
                <h4 className="font-bold text-green-800">サイドバー</h4>
              </div>
              <ul className="text-sm text-green-700 space-y-1">
                <li>・各機能へのメニュー</li>
                <li>・現在のページは<strong>色付き</strong>で表示</li>
                <li>・クリックでページ移動</li>
                <li>・スマホでは左上のメニューボタン</li>
              </ul>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <span className="bg-purple-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold text-sm mr-2">3</span>
                <h4 className="font-bold text-purple-800">メインエリア</h4>
              </div>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>・各機能の本体部分</li>
                <li>・グラフや表が表示される</li>
                <li>・設定や操作もここで行う</li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-bold text-yellow-800 mb-2 flex items-center">
              <Info className="w-5 h-5 mr-2" />
              スマートフォンでの表示
            </h4>
            <p className="text-yellow-700">
              スマートフォンで見ると、サイドバーは隠れています。
              <strong>左上の三本線（≡）</strong>をタップするとメニューが開きます。
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'dashboard',
      title: 'ダッシュボード - 全体を把握する',
      icon: <LayoutDashboard className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-lg">
              <strong>ダッシュボード</strong>は、ログインして最初に表示される画面です。
              法人全体の状況を<strong>ひと目で</strong>確認できます。
            </p>
            <div className="mt-3 bg-white/70 rounded p-3 text-sm">
              <p className="text-blue-700">
                <strong>こんな人におすすめ：</strong>毎日の数字をサッと確認したい方、全体の傾向を把握したい経営者・マネージャーの方
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-bold text-yellow-800 mb-2">用語解説</h4>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="bg-white rounded p-2">
                <strong>サマリー</strong>：重要な数字をまとめた概要のこと
              </div>
              <div className="bg-white rounded p-2">
                <strong>推移グラフ</strong>：時間の経過とともに数値がどう変化したかを示すグラフ
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-bold text-lg flex items-center">
              <Eye className="w-5 h-5 mr-2 text-primary" />
              画面の構成要素
            </h4>

            <div className="bg-gray-50 rounded-lg p-5 space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h5 className="font-bold text-gray-800">① 法人選択（左上）</h5>
                <p className="text-gray-600 mt-1">
                  複数の法人にアクセス権がある場合、ここで切り替えます。<br />
                  ドロップダウンをクリック → 見たい法人を選択
                </p>
                <div className="mt-2 bg-white rounded p-2 text-sm text-gray-500">
                  ※ 1つの法人しかアクセス権がない場合は、その法人が自動で選択されます
                </div>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <h5 className="font-bold text-gray-800">② 期間選択</h5>
                <p className="text-gray-600 mt-1">
                  データを表示する期間を設定します。<br />
                  <strong>開始日</strong>と<strong>終了日</strong>をカレンダーから選択
                </p>
                <div className="mt-2 bg-white rounded p-3 border text-sm">
                  <p className="text-gray-500 font-medium">よく使う期間の例：</p>
                  <ul className="mt-1 space-y-1">
                    <li>・<strong>今月のデータ</strong> → 今月1日 〜 今日（月の途中経過を見る）</li>
                    <li>・<strong>先月のデータ</strong> → 先月1日 〜 先月末日（月次レポート用）</li>
                    <li>・<strong>直近1週間</strong> → 7日前 〜 今日（週次チェック用）</li>
                    <li>・<strong>直近3ヶ月</strong> → 3ヶ月前 〜 今日（トレンド分析用）</li>
                  </ul>
                </div>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <h5 className="font-bold text-gray-800">③ サマリーカード</h5>
                <p className="text-gray-600 mt-1">
                  重要な数字が大きく表示されます。一目で現状を把握できます。
                </p>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="bg-white rounded p-3 border text-center">
                    <p className="text-xs text-gray-500">総登録数</p>
                    <p className="text-xl font-bold text-primary">1,234</p>
                    <p className="text-xs text-gray-400">期間内の登録者数</p>
                  </div>
                  <div className="bg-white rounded p-3 border text-center">
                    <p className="text-xs text-gray-500">タップ数</p>
                    <p className="text-xl font-bold text-green-600">567</p>
                    <p className="text-xs text-gray-400">リンククリック数</p>
                  </div>
                  <div className="bg-white rounded p-3 border text-center">
                    <p className="text-xs text-gray-500">クリック率</p>
                    <p className="text-xl font-bold text-blue-600">45.9%</p>
                    <p className="text-xs text-gray-400">登録者のうち何%がクリック</p>
                  </div>
                  <div className="bg-white rounded p-3 border text-center">
                    <p className="text-xs text-gray-500">予約数</p>
                    <p className="text-xl font-bold text-orange-600">89</p>
                    <p className="text-xs text-gray-400">予約完了数</p>
                  </div>
                </div>
              </div>

              <div className="border-l-4 border-orange-500 pl-4">
                <h5 className="font-bold text-gray-800">④ グラフエリア</h5>
                <p className="text-gray-600 mt-1">
                  日々の推移が<strong>折れ線グラフ</strong>で表示されます。<br />
                  グラフにマウスを乗せると、その日の詳細数値が表示されます。
                </p>
                <div className="mt-2 bg-white rounded p-2 text-sm text-gray-500">
                  ※ スマホの場合は、グラフをタップすると詳細が表示されます
                </div>
              </div>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5">
            <h4 className="font-bold text-indigo-800 mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2" />
              ダッシュボードで何がわかる？
            </h4>
            <div className="space-y-3 text-indigo-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">全体の健康状態</p>
                <p className="text-sm">登録者数やクリック率が順調に推移しているか、一目で確認できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">異常値の早期発見</p>
                <p className="text-sm">急激な増減があれば、何か問題（または成功した施策）があった可能性があります</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">曜日や時期のパターン</p>
                <p className="text-sm">週末に登録が増える、月末に減る、などのパターンを発見できます</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <h4 className="font-bold text-green-800 mb-3 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              ダッシュボードの活用ポイント
            </h4>
            <ul className="space-y-2 text-green-700">
              <li className="flex items-start">
                <ArrowRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <span><strong>毎朝チェック</strong>：前日のデータを確認して、異常がないか確認</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <span><strong>急な変化に注目</strong>：グラフが急に上がった/下がった日は、何か施策があったはず</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <span><strong>期間を変えて比較</strong>：今月と先月を見比べて、成長しているか確認</span>
              </li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
            <h4 className="font-bold text-purple-800 mb-3 flex items-center">
              <Target className="w-5 h-5 mr-2" />
              分析結果を何に活かせる？
            </h4>
            <div className="space-y-3 text-purple-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">朝礼や定例会議での報告</p>
                <p className="text-sm">「昨日の登録者は〇〇人でした」と、最新の数字をすぐに報告できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">施策の効果測定</p>
                <p className="text-sm">広告を出した日の前後で数字がどう変わったかを確認できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">目標達成の進捗確認</p>
                <p className="text-sm">「今月の目標1000人に対して、今どこまで達成しているか」を確認できます</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'analysis',
      title: 'データ分析 - ラベルごとの詳細',
      icon: <BarChart3 className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-lg">
              <strong>データ分析</strong>では、ラベル（タグ）ごとの詳細なデータを確認できます。
              どのラベルが増えているか、減っているかがわかります。
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4 flex items-center">
              <PlayCircle className="w-5 h-5 mr-2 text-primary" />
              操作手順（ステップバイステップ）
            </h4>
            <div className="space-y-4">
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">1</span>
                <div>
                  <p className="font-bold">法人を選ぶ</p>
                  <p className="text-gray-600 text-sm mt-1">
                    画面上部の「法人」ドロップダウンから、分析したい法人を選択します。
                  </p>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">2</span>
                <div>
                  <p className="font-bold">期間を設定する</p>
                  <p className="text-gray-600 text-sm mt-1">
                    「開始日」と「終了日」を選びます。カレンダーアイコンをクリックすると日付選択画面が開きます。
                  </p>
                  <div className="mt-2 flex items-center space-x-2 text-sm">
                    <span className="bg-gray-100 px-3 py-1 rounded flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />2024/01/01
                    </span>
                    <span>〜</span>
                    <span className="bg-gray-100 px-3 py-1 rounded flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />2024/01/31
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">3</span>
                <div>
                  <p className="font-bold">アカウントを選ぶ（任意）</p>
                  <p className="text-gray-600 text-sm mt-1">
                    特定のLINEアカウントやメールアカウントだけを見たい場合は選択します。
                    空欄のままだと全アカウントが対象になります。
                  </p>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">4</span>
                <div>
                  <p className="font-bold">「分析実行」ボタンをクリック</p>
                  <p className="text-gray-600 text-sm mt-1">
                    設定が完了したら、青い「分析実行」ボタンをクリックします。
                    数秒待つとグラフと表が表示されます。
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-bold text-lg">表示されるデータの見方</h4>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white border rounded-lg p-4">
                <h5 className="font-bold text-gray-800 mb-2">グラフ（上部）</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>・横軸 = 日付</li>
                  <li>・縦軸 = 数値（人数など）</li>
                  <li>・線の色 = ラベルごとに異なる</li>
                  <li>・マウスを乗せると詳細表示</li>
                </ul>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <h5 className="font-bold text-gray-800 mb-2">表（下部）</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>・ラベル名ごとの数値一覧</li>
                  <li>・列をクリックで並び替え</li>
                  <li>・日次値と累計値を表示</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-bold text-yellow-800 mb-2 flex items-center">
              <Download className="w-5 h-5 mr-2" />
              データのダウンロード
            </h4>
            <p className="text-yellow-700">
              「CSVエクスポート」または「Excelエクスポート」ボタンをクリックすると、
              表示中のデータをファイルでダウンロードできます。
              Excelで開いてさらに分析したり、報告書に貼り付けたりできます。
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'cross-analysis',
      title: 'クロス分析 - 2つの軸で分析',
      icon: <Grid3X3 className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-lg">
              <strong>クロス分析</strong>は、2つの項目を掛け合わせて分析できます。
              例えば「月 × カテゴリ」で、どの月にどのカテゴリが多いかがわかります。
            </p>
            <div className="mt-3 bg-white/70 rounded p-3 text-sm">
              <p className="text-blue-700">
                <strong>こんな人におすすめ：</strong>複数の切り口でデータを比較したい方、どの組み合わせが効果的か知りたい方
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-bold text-yellow-800 mb-2">用語解説</h4>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="bg-white rounded p-2">
                <strong>クロス分析</strong>：2つの項目を縦軸・横軸にした表を作り、組み合わせごとの数値を確認する分析
              </div>
              <div className="bg-white rounded p-2">
                <strong>ヒートマップ</strong>：数値の大きさを色の濃さで表現する表示方法。青が濃いほど数値が大きい
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
            <h4 className="font-bold text-purple-800 mb-4">クロス分析のイメージ</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse bg-white rounded">
                <thead>
                  <tr className="bg-purple-100">
                    <th className="border p-2"></th>
                    <th className="border p-2">LINE公式</th>
                    <th className="border p-2">メルマガ</th>
                    <th className="border p-2">合計</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border p-2 font-bold bg-purple-50">送信済</td>
                    <td className="border p-2 text-center bg-blue-200">1,200</td>
                    <td className="border p-2 text-center bg-blue-100">800</td>
                    <td className="border p-2 text-center font-bold">2,000</td>
                  </tr>
                  <tr>
                    <td className="border p-2 font-bold bg-purple-50">タップ</td>
                    <td className="border p-2 text-center bg-blue-100">600</td>
                    <td className="border p-2 text-center">200</td>
                    <td className="border p-2 text-center font-bold">800</td>
                  </tr>
                  <tr>
                    <td className="border p-2 font-bold bg-purple-50">予約</td>
                    <td className="border p-2 text-center">120</td>
                    <td className="border p-2 text-center">80</td>
                    <td className="border p-2 text-center font-bold">200</td>
                  </tr>
                  <tr className="bg-purple-100">
                    <td className="border p-2 font-bold">合計</td>
                    <td className="border p-2 text-center font-bold">1,920</td>
                    <td className="border p-2 text-center font-bold">1,080</td>
                    <td className="border p-2 text-center font-bold">3,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-purple-700 text-sm mt-3">
              ↑ この例では「カテゴリ（縦軸）× アカウント（横軸）」でクロス分析しています。<br />
              青色が濃いセル = 数値が大きい = 注目ポイント
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4 flex items-center">
              <PlayCircle className="w-5 h-5 mr-2" />
              使い方（とても簡単！）
            </h4>
            <div className="space-y-4">
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">1</span>
                <div>
                  <p className="font-bold">法人と期間を選ぶ</p>
                  <p className="text-gray-600 text-sm mt-1">
                    画面上部で、分析したい法人と期間（開始日〜終了日）を設定します
                  </p>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border border-primary bg-primary/5">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">2</span>
                <div>
                  <p className="font-bold">分析パターンをクリック（即実行！）</p>
                  <p className="text-gray-600 text-sm mt-1">
                    よく使う4つのパターンが用意されています。<strong>クリックするだけで自動的に分析が実行されます。</strong>
                  </p>
                  <div className="mt-3 bg-gray-50 rounded p-3 text-sm">
                    <p className="font-medium mb-2">用意されているパターン：</p>
                    <ul className="space-y-1 text-gray-600">
                      <li>・<strong>月別×カテゴリ</strong>：月ごとの登録・タップ・予約の推移</li>
                      <li>・<strong>アカウント別×カテゴリ</strong>：LINE vs メールの比較</li>
                      <li>・<strong>シナリオ別×チャネル</strong>：流入経路の効果比較</li>
                      <li>・<strong>週別×アカウント</strong>：週単位でのパフォーマンス推移</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">3</span>
                <div>
                  <p className="font-bold">結果を確認</p>
                  <p className="text-gray-600 text-sm mt-1">
                    数値が大きいセルは青色で表示されます。どこに注目すべきかが一目でわかります。
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="font-bold text-orange-800 mb-2">カスタム設定について</h4>
            <p className="text-orange-700 text-sm">
              用意されたパターン以外の組み合わせで分析したい場合は、「自分で軸を選ぶ（カスタム設定）」を開いて設定できます。
              カスタム設定を変更した後は「再分析」ボタンをクリックしてください。
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <h4 className="font-bold text-green-800 mb-3">選べる軸の説明</h4>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-white rounded p-3 border">
                <p className="font-bold text-gray-800">カテゴリ</p>
                <p className="text-sm text-gray-600">送信済、タップ、予約など、行動の種類</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-bold text-gray-800">アカウント</p>
                <p className="text-sm text-gray-600">LINE公式、メルマガなど</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-bold text-gray-800">月</p>
                <p className="text-sm text-gray-600">1月、2月、3月...のように月単位</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-bold text-gray-800">週</p>
                <p className="text-sm text-gray-600">第1週、第2週...のように週単位</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-bold text-gray-800">シナリオ</p>
                <p className="text-sm text-gray-600">オプトイン、個別案内など流入経路</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-bold text-gray-800">チャネル</p>
                <p className="text-sm text-gray-600">LINE か メール か</p>
              </div>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5">
            <h4 className="font-bold text-indigo-800 mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2" />
              クロス分析で何がわかる？
            </h4>
            <div className="space-y-3 text-indigo-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">成長しているカテゴリ</p>
                <p className="text-sm">月別×カテゴリで、どの行動が増えているか減っているかがわかります</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">チャネル別の効果</p>
                <p className="text-sm">LINEとメール、どちらの反応が良いかを比較できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">効果的な流入経路</p>
                <p className="text-sm">シナリオ別に見ると、どの経路からの登録者が多いかがわかります</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
            <h4 className="font-bold text-purple-800 mb-3 flex items-center">
              <Target className="w-5 h-5 mr-2" />
              分析結果を何に活かせる？
            </h4>
            <div className="space-y-3 text-purple-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">リソース配分の最適化</p>
                <p className="text-sm">効果の高いアカウントやチャネルにリソースを集中できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">弱点の発見</p>
                <p className="text-sm">数値が低い組み合わせを特定し、改善に取り組めます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">レポート作成</p>
                <p className="text-sm">CSV出力して、Excelでさらに加工したり報告書に添付できます</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'funnel-analysis',
      title: 'ファネル分析 - 離脱ポイントを見つける',
      icon: <Filter className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-lg">
              <strong>ファネル分析</strong>は、お客さんが「登録 → クリック → 予約 → 成約」と
              進んでいく流れを視覚的に確認できます。<strong>どこで人が減っているか</strong>がわかります。
            </p>
            <div className="mt-3 bg-white/70 rounded p-3 text-sm">
              <p className="text-blue-700">
                <strong>こんな人におすすめ：</strong>「なぜ成約率が低いのか」を知りたい方、マーケティングの改善ポイントを見つけたい方
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-bold text-yellow-800 mb-2">用語解説</h4>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="bg-white rounded p-2">
                <strong>ファネル</strong>：漏斗（じょうご）のこと。最初は多くの人がいても、ゴールに向かうにつれて減っていく様子を表します
              </div>
              <div className="bg-white rounded p-2">
                <strong>離脱率</strong>：あるステップから次のステップに進まなかった人の割合
              </div>
              <div className="bg-white rounded p-2">
                <strong>転換率（CV率）</strong>：あるステップから次のステップに進んだ人の割合（離脱率の逆）
              </div>
              <div className="bg-white rounded p-2">
                <strong>ラベルパターン</strong>：ラベル名に含まれるキーワード。部分一致で検索されます
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-5">
            <h4 className="font-bold text-orange-800 mb-4">ファネルのイメージ（視覚的に理解しよう）</h4>
            <div className="flex flex-col items-center space-y-2">
              <div className="bg-blue-500 text-white py-3 px-8 rounded text-center" style={{width: '100%'}}>
                <p className="font-bold">登録</p>
                <p className="text-2xl">1,000人</p>
              </div>
              <div className="text-orange-600 text-sm">↓ 50%が離脱（500人が次に進まなかった）</div>
              <div className="bg-blue-400 text-white py-3 px-8 rounded text-center" style={{width: '75%'}}>
                <p className="font-bold">クリック</p>
                <p className="text-2xl">500人</p>
              </div>
              <div className="text-red-600 text-sm font-bold">↓ 60%が離脱 ← 最大の問題点！</div>
              <div className="bg-blue-300 text-white py-3 px-8 rounded text-center" style={{width: '50%'}}>
                <p className="font-bold">予約</p>
                <p className="text-2xl">200人</p>
              </div>
              <div className="text-orange-600 text-sm">↓ 25%が離脱</div>
              <div className="bg-blue-200 text-blue-800 py-3 px-8 rounded text-center" style={{width: '35%'}}>
                <p className="font-bold">成約</p>
                <p className="text-2xl">150人</p>
              </div>
            </div>
            <div className="mt-4 bg-white rounded p-3">
              <p className="text-orange-700 text-sm">
                <strong>この例でわかること：</strong><br />
                ・登録から成約までの全体の転換率は15%（1000人中150人）<br />
                ・「クリック→予約」で60%も離脱している = ここが最大の改善ポイント<br />
                ・予約ページの内容や、クリック後のメッセージを改善する必要がある
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4 flex items-center">
              <PlayCircle className="w-5 h-5 mr-2" />
              使い方（ステップバイステップ）
            </h4>

            <div className="mb-4 p-3 bg-blue-50 rounded">
              <p className="text-blue-700 text-sm">
                <strong>前提：</strong>最初に「ファネル定義」を作成する必要があります。
                一度作成すれば、何度でも分析に使えます。
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">1</span>
                <div>
                  <p className="font-bold">「ファネル定義を作成」ボタンをクリック</p>
                  <p className="text-gray-600 text-sm mt-1">
                    画面上部にあるボタンです。既存の定義がある場合は、それを選んで分析することもできます。
                  </p>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">2</span>
                <div>
                  <p className="font-bold">ファネル名を入力</p>
                  <p className="text-gray-600 text-sm mt-1">
                    後で見返したときにわかりやすい名前をつけます
                  </p>
                  <div className="mt-2 bg-gray-50 rounded p-2 text-sm">
                    <p className="text-gray-500">名前の例：</p>
                    <ul className="mt-1 space-y-1">
                      <li>・「メインLINE_セミナー申込フロー」</li>
                      <li>・「商品A_購入フロー」</li>
                      <li>・「無料相談_申込フロー」</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">3</span>
                <div>
                  <p className="font-bold">ステップを追加（重要！）</p>
                  <p className="text-gray-600 text-sm mt-1">
                    各ステップに「名前」と「ラベルパターン」を設定します。<br />
                    ラベルパターンは、UTAGEで使っているラベル名の一部を入力します。
                  </p>
                  <div className="mt-3 bg-gray-50 rounded p-3 text-sm">
                    <p className="font-medium mb-2">設定例：</p>
                    <table className="w-full text-left">
                      <thead className="bg-gray-200">
                        <tr>
                          <th className="p-2">ステップ名</th>
                          <th className="p-2">ラベルパターン</th>
                          <th className="p-2">説明</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="p-2">登録</td>
                          <td className="p-2 font-mono text-blue-600">送信済</td>
                          <td className="p-2 text-gray-500">LINE/メール登録した人</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-2">クリック</td>
                          <td className="p-2 font-mono text-blue-600">タップ</td>
                          <td className="p-2 text-gray-500">リンクをクリックした人</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-2">予約</td>
                          <td className="p-2 font-mono text-blue-600">予約</td>
                          <td className="p-2 text-gray-500">予約フォームを送信した人</td>
                        </tr>
                        <tr>
                          <td className="p-2">成約</td>
                          <td className="p-2 font-mono text-blue-600">成約</td>
                          <td className="p-2 text-gray-500">購入・契約した人</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">4</span>
                <div>
                  <p className="font-bold">「保存」をクリック</p>
                  <p className="text-gray-600 text-sm mt-1">
                    保存すると、ファネル定義一覧に追加されます
                  </p>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">5</span>
                <div>
                  <p className="font-bold">ファネルを選んで「分析」をクリック</p>
                  <p className="text-gray-600 text-sm mt-1">
                    作成したファネル定義を選び、期間を指定して分析を実行します
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-bold text-yellow-800 mb-2 flex items-center">
              <Info className="w-5 h-5 mr-2" />
              ラベルパターンとは？（詳しく解説）
            </h4>
            <p className="text-yellow-700">
              ラベル名に含まれる<strong>キーワード</strong>のことです。
              <strong>部分一致</strong>で検索されるので、完全に一致する必要はありません。
            </p>
            <div className="mt-3 bg-white rounded p-3">
              <p className="text-sm text-yellow-700 font-medium">例：「タップ」と入力した場合</p>
              <p className="text-sm text-yellow-700 mt-1">以下のようなラベルがすべて対象になります：</p>
              <ul className="mt-2 text-yellow-700 text-sm space-y-1">
                <li>・LINE1_オプトイン_<strong className="bg-yellow-200">タップ</strong></li>
                <li>・メール_個別案内_<strong className="bg-yellow-200">タップ</strong>_リンク1</li>
                <li>・<strong className="bg-yellow-200">タップ</strong>済み</li>
                <li>・商品A_<strong className="bg-yellow-200">タップ</strong>_電子書籍</li>
              </ul>
            </div>
            <div className="mt-3 bg-red-50 rounded p-3">
              <p className="text-sm text-red-700">
                <strong>注意：</strong>パターンが曖昧すぎると意図しないラベルも含まれることがあります。
                例えば「予」だけだと「予約」も「予告」も含まれます。
              </p>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5">
            <h4 className="font-bold text-indigo-800 mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2" />
              ファネル分析で何がわかる？
            </h4>
            <div className="space-y-3 text-indigo-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">最大の離脱ポイント</p>
                <p className="text-sm">どのステップで最も多くの人が離脱しているかがわかります。これが改善の最優先箇所です。</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">全体の転換率</p>
                <p className="text-sm">最初のステップから最後のステップまで、何%の人が到達しているかがわかります。</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">ボトルネック（詰まっている場所）</p>
                <p className="text-sm">流れがスムーズでない箇所を特定できます。</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <h4 className="font-bold text-green-800 mb-3 flex items-center">
              <Target className="w-5 h-5 mr-2" />
              分析結果を何に活かせる？
            </h4>
            <div className="space-y-3 text-green-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">具体的な改善アクション</p>
                <ul className="text-sm mt-1 space-y-1">
                  <li>・登録→クリック の離脱が多い → メッセージの内容やタイミングを改善</li>
                  <li>・クリック→予約 の離脱が多い → ランディングページの改善、特典の追加</li>
                  <li>・予約→成約 の離脱が多い → セールストークの改善、フォローアップ強化</li>
                </ul>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">リソース配分の最適化</p>
                <p className="text-sm">最も改善効果が高い箇所に人員や予算を集中できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">施策の効果測定</p>
                <p className="text-sm">改善施策の前後でファネルを比較し、効果があったか確認できます</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'url-click-analysis',
      title: 'URLクリック分析 - リンクの効果測定',
      icon: <MousePointer2 className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-lg">
              LINEやメールに貼った<strong>リンクがどれくらいクリックされているか</strong>を分析できます。
              どのリンクが人気か、どのリンクが見られていないかがわかります。
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4">操作手順</h4>
            <div className="space-y-3">
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">1</span>
                <p>法人と期間を選ぶ</p>
              </div>
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">2</span>
                <p>「分析実行」をクリック</p>
              </div>
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">3</span>
                <p>円グラフとランキング表で確認</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <h5 className="font-bold text-gray-800 mb-3">円グラフ</h5>
              <p className="text-gray-600 text-sm">
                各リンクのクリック数の割合を視覚的に表示。
                どのリンクが全体の何%を占めているかがわかります。
              </p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <h5 className="font-bold text-gray-800 mb-3">ランキング表</h5>
              <p className="text-gray-600 text-sm">
                クリック数の多い順にリンクを一覧表示。
                具体的な数値と割合を確認できます。
              </p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-bold text-green-800 mb-2 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2" />
              分析のポイント
            </h4>
            <ul className="text-green-700 space-y-2">
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <span><strong>クリック数が多いリンク</strong> = 興味を持たれている内容。似た内容を増やすと効果的</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <span><strong>クリック数が少ないリンク</strong> = タイトルや説明文を改善する必要あり</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <span>チャネル別（LINE/メール）で違いがあれば、それぞれに最適化</span>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'scoring',
      title: '行動スコア - 活発度を数値化',
      icon: <Trophy className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-lg">
              お客さんの行動に<strong>ポイント（スコア）</strong>をつけて、
              どのステップが一番活発かを数値化できます。
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
            <h4 className="font-bold text-yellow-800 mb-3">スコアの考え方</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm bg-white rounded border">
                <thead className="bg-yellow-100">
                  <tr>
                    <th className="p-3 text-left">行動</th>
                    <th className="p-3 text-center">スコア</th>
                    <th className="p-3 text-left">理由</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3">タップ（クリック）</td>
                    <td className="p-3 text-center font-bold text-blue-600">10点</td>
                    <td className="p-3 text-gray-600">興味を示した</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">資料請求</td>
                    <td className="p-3 text-center font-bold text-blue-600">30点</td>
                    <td className="p-3 text-gray-600">積極的に情報を求めた</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">予約</td>
                    <td className="p-3 text-center font-bold text-blue-600">50点</td>
                    <td className="p-3 text-gray-600">行動に移した</td>
                  </tr>
                  <tr>
                    <td className="p-3">成約・購入</td>
                    <td className="p-3 text-center font-bold text-blue-600">100点</td>
                    <td className="p-3 text-gray-600">最終目標達成</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4">3つのタブの使い方</h4>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border">
                <h5 className="font-bold text-gray-800 flex items-center">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm mr-2">タブ1</span>
                  スコアルール
                </h5>
                <p className="text-gray-600 mt-2">
                  どの行動に何点つけるかのルールを作成・編集します。
                  「新規作成」ボタンでルールを追加。
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h5 className="font-bold text-gray-800 flex items-center">
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm mr-2">タブ2</span>
                  スコア計算
                </h5>
                <p className="text-gray-600 mt-2">
                  作成したルールを選んで、期間を指定してスコアを計算。
                  カテゴリ別の内訳も確認できます。
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h5 className="font-bold text-gray-800 flex items-center">
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm mr-2">タブ3</span>
                  ランキング
                </h5>
                <p className="text-gray-600 mt-2">
                  スコアが高い順にステップをランキング表示。
                  どのステップが最も活発かがわかります。
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'inflow-analysis',
      title: '流入経路分析 - どこから来たか',
      icon: <Route className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-lg">
              お客さんが<strong>どこから来ているか</strong>（流入経路）を分析できます。
              「オプトイン」「個別案内」「広告」など、どの経路が多いかがわかります。
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4">操作手順</h4>
            <div className="space-y-3">
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">1</span>
                <p>法人と期間を選ぶ</p>
              </div>
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">2</span>
                <p>「分析実行」をクリック</p>
              </div>
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">3</span>
                <p>円グラフで全体の割合を確認</p>
              </div>
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">4</span>
                <p>表でシナリオごとの詳細を確認</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-bold text-green-800 mb-2 flex items-center">
              <Info className="w-5 h-5 mr-2" />
              シナリオとは？
            </h4>
            <p className="text-green-700">
              UTAGEで設定した「オプトイン」「個別案内」などの名前のことです。
              ラベル名から自動で抽出されます。
            </p>
            <div className="mt-3 bg-white rounded p-3 text-sm">
              <p className="font-medium mb-2">例：</p>
              <ul className="space-y-1 text-gray-600">
                <li>「LINE1_<strong>オプトイン</strong>_登録」→ シナリオは「オプトイン」</li>
                <li>「メール_<strong>個別案内</strong>_タップ」→ シナリオは「個別案内」</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'conversion',
      title: 'コンバージョン - 目標達成率',
      icon: <Target className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-lg">
              <strong>コンバージョン</strong>とは目標達成のこと。
              例えば「登録した人のうち、何%が予約したか」を計算できます。
            </p>
            <div className="mt-3 bg-white/70 rounded p-3 text-sm">
              <p className="text-blue-700">
                <strong>こんな人におすすめ：</strong>KPIを数値で管理したい方、目標達成率を定期的に追跡したい方
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-bold text-yellow-800 mb-2">用語解説</h4>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="bg-white rounded p-2">
                <strong>コンバージョン（CV）</strong>：目標とする行動を達成すること。「予約」「購入」「問い合わせ」など。
              </div>
              <div className="bg-white rounded p-2">
                <strong>CVR（コンバージョン率）</strong>：起点となる行動から、目標行動に至った人の割合。
              </div>
              <div className="bg-white rounded p-2">
                <strong>起点</strong>：CVRを計算する際の分母となる行動。例：「登録」
              </div>
              <div className="bg-white rounded p-2">
                <strong>ゴール</strong>：CVRを計算する際の分子となる行動。例：「予約」「購入」
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-5">
            <h4 className="font-bold text-red-800 mb-3">CVR（コンバージョン率）の計算</h4>
            <div className="bg-white rounded-lg p-4 text-center">
              <p className="text-2xl font-mono">
                CVR = <span className="text-red-600">ゴール達成数</span> ÷ <span className="text-blue-600">起点数</span> × 100
              </p>
              <div className="mt-4 text-left bg-gray-50 rounded p-3">
                <p className="font-medium mb-2">計算例：</p>
                <ul className="text-sm space-y-1">
                  <li>・起点（送信済）：<strong>100人</strong>（LINE登録した人）</li>
                  <li>・ゴール（予約）：<strong>20人</strong>（予約した人）</li>
                  <li>・CVR = 20 ÷ 100 × 100 = <strong className="text-red-600">20%</strong></li>
                </ul>
                <p className="text-gray-600 text-sm mt-3">
                  → 登録した人のうち20%が予約まで進んだ、ということがわかります
                </p>
              </div>
            </div>
            <div className="mt-3 bg-white rounded p-3 text-sm">
              <p className="text-red-700">
                <strong>業界目安：</strong><br />
                ・EC（通販）のCVR：1〜3%<br />
                ・リード獲得（資料請求等）のCVR：2〜5%<br />
                ・セミナー申込のCVR：5〜15%<br />
                ※ 業種や商材によって大きく異なります
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4 flex items-center">
              <PlayCircle className="w-5 h-5 mr-2" />
              使い方（ステップバイステップ）
            </h4>

            <div className="mb-4 p-3 bg-blue-50 rounded">
              <p className="text-blue-700 text-sm">
                <strong>前提：</strong>最初に「コンバージョンゴール」を作成する必要があります。
                一度作成すれば、何度でも分析に使えます。
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border">
                <h5 className="font-bold text-gray-800 flex items-center">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm mr-2">タブ1</span>
                  ゴール管理
                </h5>
                <p className="text-gray-600 mt-2">
                  コンバージョンゴールを作成・編集します。<br />
                  <strong>起点パターン</strong>と<strong>ゴールパターン</strong>を設定。
                </p>
                <div className="mt-3 bg-gray-50 rounded p-3 text-sm">
                  <p className="font-medium mb-2">設定例：</p>
                  <table className="w-full text-left">
                    <thead className="bg-gray-200">
                      <tr>
                        <th className="p-2">ゴール名</th>
                        <th className="p-2">起点パターン</th>
                        <th className="p-2">ゴールパターン</th>
                        <th className="p-2">説明</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="p-2">登録→予約</td>
                        <td className="p-2 font-mono text-blue-600">送信済</td>
                        <td className="p-2 font-mono text-red-600">予約</td>
                        <td className="p-2 text-gray-500">登録者の予約率</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2">クリック→予約</td>
                        <td className="p-2 font-mono text-blue-600">タップ</td>
                        <td className="p-2 font-mono text-red-600">予約</td>
                        <td className="p-2 text-gray-500">クリックした人の予約率</td>
                      </tr>
                      <tr>
                        <td className="p-2">予約→成約</td>
                        <td className="p-2 font-mono text-blue-600">予約</td>
                        <td className="p-2 font-mono text-red-600">成約</td>
                        <td className="p-2 text-gray-500">予約者の成約率</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 bg-yellow-50 rounded p-2 text-sm text-yellow-700">
                  <strong>ポイント：</strong>複数のゴールを作っておくと、ファネルの各段階のCVRを比較できます
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h5 className="font-bold text-gray-800 flex items-center">
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm mr-2">タブ2</span>
                  サマリー
                </h5>
                <p className="text-gray-600 mt-2">
                  全ゴールの達成率を一覧表示。
                  期間を指定して「取得」ボタンをクリック。
                </p>
                <div className="mt-3 bg-gray-50 rounded p-3">
                  <p className="text-sm text-gray-600">
                    <strong>表示される情報：</strong>
                  </p>
                  <ul className="text-sm text-gray-600 mt-1 space-y-1">
                    <li>・ゴール名</li>
                    <li>・起点数（分母）</li>
                    <li>・ゴール達成数（分子）</li>
                    <li>・CVR（%）</li>
                  </ul>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h5 className="font-bold text-gray-800 flex items-center">
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm mr-2">タブ3</span>
                  推移
                </h5>
                <p className="text-gray-600 mt-2">
                  CVRの日別・週別・月別の変化をグラフで確認。
                  ゴールを選んで「取得」ボタンをクリック。
                </p>
                <div className="mt-3 bg-gray-50 rounded p-3">
                  <p className="text-sm text-gray-600">
                    <strong>グラフの見方：</strong>
                  </p>
                  <ul className="text-sm text-gray-600 mt-1 space-y-1">
                    <li>・右肩上がり = 改善している</li>
                    <li>・右肩下がり = 悪化している</li>
                    <li>・大きな変動 = 何か施策や問題があった可能性</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5">
            <h4 className="font-bold text-indigo-800 mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2" />
              コンバージョン分析で何がわかる？
            </h4>
            <div className="space-y-3 text-indigo-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">マーケティングの効率</p>
                <p className="text-sm">広告や施策に対して、どれくらいの人が目標行動を取っているかがわかります</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">改善の効果</p>
                <p className="text-sm">施策の前後でCVRを比較し、本当に効果があったのか数値で確認できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">季節変動やトレンド</p>
                <p className="text-sm">推移グラフを見ることで、CVRが上がる時期・下がる時期のパターンがわかります</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <h4 className="font-bold text-green-800 mb-3 flex items-center">
              <Target className="w-5 h-5 mr-2" />
              分析結果を何に活かせる？
            </h4>
            <div className="space-y-3 text-green-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">KPI管理</p>
                <p className="text-sm">「今月のCVR目標は20%」といった目標を設定し、達成状況を追跡できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">売上予測</p>
                <p className="text-sm">登録者数 × CVR × 単価 で、おおよその売上を予測できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">施策の優先順位付け</p>
                <p className="text-sm">CVRが低い箇所を特定し、改善の優先順位を決められます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">広告費の最適化</p>
                <p className="text-sm">CVRが高いチャネルに広告費を集中させ、効率を上げられます</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'ai-analysis',
      title: 'AI分析 - AIがアドバイス',
      icon: <Lightbulb className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-lg">
              AIがデータを自動で分析して、<strong>改善点やアドバイス</strong>を教えてくれます。
              数字を見るだけではわからない「気づき」を得られます。
            </p>
            <div className="mt-3 bg-white/70 rounded p-3 text-sm">
              <p className="text-blue-700">
                <strong>こんな人におすすめ：</strong>データの見方がわからない方、専門家の意見が欲しい方、改善ポイントを自動で見つけたい方
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-bold text-yellow-800 mb-2">用語解説</h4>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="bg-white rounded p-2">
                <strong>AI分析</strong>：人工知能がデータを読み取り、パターンや改善点を自動で見つけてくれる機能
              </div>
              <div className="bg-white rounded p-2">
                <strong>月次サマリー</strong>：1ヶ月のデータをまとめて、傾向と改善点を報告してくれる分析
              </div>
              <div className="bg-white rounded p-2">
                <strong>トレンド分析</strong>：データの上昇・下降傾向を分析し、今後の予測を立てる分析
              </div>
              <div className="bg-white rounded p-2">
                <strong>キャッシュ</strong>：一度分析した結果を保存しておき、再度同じ分析を高速に表示する仕組み
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4 flex items-center">
              <PlayCircle className="w-5 h-5 mr-2" />
              使い方（ステップバイステップ）
            </h4>
            <div className="space-y-3">
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">1</span>
                <div>
                  <p className="font-bold">法人と期間を選ぶ</p>
                  <p className="text-gray-600 text-sm mt-1">
                    分析したい法人をドロップダウンから選び、開始日と終了日を設定します
                  </p>
                  <div className="mt-2 bg-gray-50 rounded p-2 text-sm text-gray-500">
                    おすすめの期間：月次サマリーなら1ヶ月、トレンド分析なら3ヶ月以上
                  </div>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">2</span>
                <div>
                  <p className="font-bold">分析タイプを選ぶ</p>
                  <div className="mt-2 space-y-2">
                    <div className="bg-gray-50 rounded p-2">
                      <p className="font-medium text-gray-800">月次サマリー</p>
                      <p className="text-sm text-gray-600">月の振り返りに最適。全体傾向、注目ポイント、改善提案をまとめてくれます</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="font-medium text-gray-800">ファネル分析</p>
                      <p className="text-sm text-gray-600">「どこで人が離脱しているか」を分析。ボトルネック特定に最適</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="font-medium text-gray-800">トレンド分析</p>
                      <p className="text-sm text-gray-600">数値の上昇・下降傾向を分析。今後の予測や季節変動の把握に最適</p>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="font-medium text-gray-800">比較分析</p>
                      <p className="text-sm text-gray-600">2つの期間を比較。施策の前後比較や、前月との比較に最適</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">3</span>
                <div>
                  <p className="font-bold">「分析実行」をクリック</p>
                  <p className="text-gray-600 text-sm mt-1">
                    ボタンを押すとAIが分析を開始します
                  </p>
                </div>
              </div>
              <div className="flex items-start bg-white rounded-lg p-4 border">
                <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">4</span>
                <div>
                  <p className="font-bold">AIからの分析結果を読む</p>
                  <p className="text-gray-600 text-sm mt-1">
                    数秒〜数十秒で結果が表示されます。結果は日本語でわかりやすく説明されます。
                  </p>
                  <div className="mt-2 bg-yellow-50 rounded p-2 text-sm text-yellow-700">
                    <strong>ヒント：</strong>結果が出たら、まず「改善提案」の部分を確認しましょう。具体的なアクションが書かれています。
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5">
            <h4 className="font-bold text-indigo-800 mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2" />
              AI分析で何がわかる？
            </h4>
            <div className="space-y-3 text-indigo-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">データの傾向</p>
                <p className="text-sm">「登録者数は増加傾向」「クリック率は横ばい」など、傾向を教えてくれます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">問題点の特定</p>
                <p className="text-sm">「〇〇の段階で離脱が多い」「△△のCVRが業界平均より低い」など、問題を指摘してくれます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">改善提案</p>
                <p className="text-sm">「メッセージのタイミングを変更してみてください」など、具体的なアクションを提案してくれます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">隠れたパターン</p>
                <p className="text-sm">「週末に登録が多い」「月末はCVRが下がる」など、気づきにくいパターンを発見してくれます</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <h4 className="font-bold text-green-800 mb-3 flex items-center">
              <Target className="w-5 h-5 mr-2" />
              分析結果を何に活かせる？
            </h4>
            <div className="space-y-3 text-green-700">
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">月次レポートの作成</p>
                <p className="text-sm">AI分析の結果をそのまま報告書に使えます。専門的な分析がすぐに手に入ります</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">改善施策の立案</p>
                <p className="text-sm">AIの改善提案をもとに、具体的な施策を計画できます</p>
              </div>
              <div className="bg-white/70 rounded p-3">
                <p className="font-medium">経営判断のサポート</p>
                <p className="text-sm">データに基づいた客観的な分析を経営判断の参考にできます</p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="font-bold text-orange-800 mb-2 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              AIへのヒントと注意点
            </h4>
            <ul className="text-orange-700 space-y-2">
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-1 flex-shrink-0 text-green-600" />
                <span>データが多いほど精度の高い分析ができます</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-1 flex-shrink-0 text-green-600" />
                <span>最低でも<strong>1ヶ月分</strong>のデータがあると良い分析ができます</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-1 flex-shrink-0 text-green-600" />
                <span>同じ期間で何度も分析するとキャッシュが使われ、すぐ結果が出ます</span>
              </li>
              <li className="flex items-start">
                <AlertTriangle className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <span>AIの提案はあくまで参考です。最終判断はご自身で行ってください</span>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'settings-account',
      title: 'アカウント設定',
      icon: <Settings className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800">
              自分のアカウント情報を確認・変更できます。
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4">できること</h4>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border">
                <h5 className="font-bold text-gray-800">パスワード変更</h5>
                <ol className="text-gray-600 mt-2 space-y-1 text-sm list-decimal list-inside">
                  <li>「現在のパスワード」を入力</li>
                  <li>「新しいパスワード」を入力</li>
                  <li>「新しいパスワード（確認）」を入力</li>
                  <li>「変更」ボタンをクリック</li>
                </ol>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <h5 className="font-bold text-gray-800">アカウント情報確認</h5>
                <p className="text-gray-600 mt-2 text-sm">
                  メールアドレス、権限（管理者/法人管理者）などを確認できます。
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'my-company',
      title: 'UTAGE連携（法人管理者向け）',
      icon: <Link className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800">
              UTAGEのログイン情報を設定できます。
              これを設定すると、<strong>毎日自動でデータを取得</strong>します。
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4">設定方法</h4>
            <div className="space-y-3">
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">1</span>
                <div>
                  <p className="font-bold">UTAGEのメールアドレスを入力</p>
                  <p className="text-gray-600 text-sm">UTAGEにログインするときのメールアドレス</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">2</span>
                <div>
                  <p className="font-bold">UTAGEのパスワードを入力</p>
                  <p className="text-gray-600 text-sm">UTAGEにログインするときのパスワード</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">3</span>
                <div>
                  <p className="font-bold">UTAGEのログインURLを入力</p>
                  <p className="text-gray-600 text-sm">通常は https://account.utage-system.com/users/login</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">4</span>
                <div>
                  <p className="font-bold">UTAGEの統計ページURLを入力</p>
                  <p className="text-gray-600 text-sm">データを取得する統計ページのURL</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm">5</span>
                <p className="font-bold">「保存」をクリック</p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-bold text-red-800 mb-2 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              セキュリティについて
            </h4>
            <p className="text-red-700">
              入力した情報は<strong>暗号化</strong>して保存されます。
              パスワードは画面上には表示されませんが、システム内部で安全に保管されています。
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'label-management',
      title: 'ラベル管理',
      icon: <Tags className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800">
              UTAGEから取得したラベルの一覧を確認・管理できます。
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-4">できること</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center mb-2">
                  <Search className="w-5 h-5 mr-2 text-gray-500" />
                  <h5 className="font-bold">ラベル検索</h5>
                </div>
                <p className="text-gray-600 text-sm">キーワードでラベルを絞り込み</p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center mb-2">
                  <Eye className="w-5 h-5 mr-2 text-gray-500" />
                  <h5 className="font-bold">有効/無効</h5>
                </div>
                <p className="text-gray-600 text-sm">不要なラベルを分析対象から外す</p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center mb-2">
                  <Edit3 className="w-5 h-5 mr-2 text-gray-500" />
                  <h5 className="font-bold">表示名変更</h5>
                </div>
                <p className="text-gray-600 text-sm">わかりやすい名前に変更</p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center mb-2">
                  <Trash2 className="w-5 h-5 mr-2 text-gray-500" />
                  <h5 className="font-bold">データ削除</h5>
                </div>
                <p className="text-gray-600 text-sm">特定のラベルのデータを削除</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'admin-features',
      title: '管理者専用機能',
      icon: <Building2 className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">
              <strong>※ この機能はシステム管理者（super_admin）のみ利用できます</strong>
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-5">
              <h4 className="font-bold text-lg mb-3 flex items-center">
                <Building2 className="w-5 h-5 mr-2" />
                法人管理
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center"><Plus className="w-4 h-4 mr-2" />法人追加：新しい法人を登録</li>
                <li className="flex items-center"><Edit3 className="w-4 h-4 mr-2" />法人編集：法人名やUTAGE連携情報を変更</li>
                <li className="flex items-center"><Trash2 className="w-4 h-4 mr-2" />法人削除：法人とそのデータを削除</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-5">
              <h4 className="font-bold text-lg mb-3 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                ユーザー管理
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center"><Plus className="w-4 h-4 mr-2" />ユーザー追加：新しいユーザーを登録</li>
                <li className="flex items-center"><Settings className="w-4 h-4 mr-2" />権限設定：管理者 or 法人管理者を選択</li>
                <li className="flex items-center"><Link className="w-4 h-4 mr-2" />法人割り当て：アクセスできる法人を設定</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-5">
              <h4 className="font-bold text-lg mb-3 flex items-center">
                <RefreshCw className="w-5 h-5 mr-2" />
                スクレイピング
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center"><PlayCircle className="w-4 h-4 mr-2" />手動実行：今すぐデータを取得</li>
                <li className="flex items-center"><RefreshCw className="w-4 h-4 mr-2" />全法人実行：全法人のデータを一括取得</li>
                <li className="flex items-center"><Settings className="w-4 h-4 mr-2" />スケジューラー：自動取得の開始/停止</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'faq',
      title: 'よくある質問（FAQ）',
      icon: <HelpCircle className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          {[
            {
              q: 'データが表示されません',
              a: '以下を確認してください：\n・正しい法人を選択しているか\n・期間内にデータがあるか\n・UTAGE連携が正しく設定されているか'
            },
            {
              q: 'パスワードを忘れました',
              a: 'システム管理者に連絡してパスワードをリセットしてもらってください。'
            },
            {
              q: 'データはいつ更新されますか？',
              a: '通常は毎日深夜に自動更新されます。最新のデータが必要な場合は、管理者に手動実行を依頼してください。'
            },
            {
              q: 'グラフをダウンロードできますか？',
              a: '各ページに「CSVエクスポート」や「Excelエクスポート」ボタンがあります。クリックするとデータをダウンロードできます。'
            },
            {
              q: 'スマホでも使えますか？',
              a: 'はい、スマホやタブレットでも使えます。画面サイズに合わせて自動でレイアウトが調整されます。左上の三本線（≡）をタップするとメニューが開きます。'
            },
            {
              q: '分析実行ボタンを押しても何も表示されません',
              a: '以下を確認してください：\n・インターネット接続が安定しているか\n・選択した期間にデータが存在するか\n・ブラウザを更新（F5キー）してみる'
            },
            {
              q: 'ファネル定義やスコアルールを間違えて作成しました',
              a: '各定義の右側にある「編集」ボタンで修正するか、「削除」ボタンで削除できます。'
            },
            {
              q: 'AIの分析結果が表示されるまで時間がかかります',
              a: 'AIの分析には数秒〜数十秒かかることがあります。同じ期間で再度分析する場合はキャッシュが使われ、すぐに結果が表示されます。'
            }
          ].map((item, index) => (
            <div key={index} className="bg-white border rounded-lg p-4">
              <p className="font-bold text-gray-800">Q: {item.q}</p>
              <p className="text-gray-600 mt-2 whitespace-pre-line">A: {item.a}</p>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'troubleshooting',
      title: 'トラブルシューティング',
      icon: <AlertTriangle className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-bold text-red-800 mb-3">問題が発生したときの対処法</h4>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border border-red-100">
                <h5 className="font-bold text-gray-800">画面が真っ白になった</h5>
                <ol className="text-gray-600 mt-2 text-sm list-decimal list-inside space-y-1">
                  <li>ブラウザを更新（F5キー または Ctrl+R）</li>
                  <li>ブラウザのキャッシュをクリア</li>
                  <li>別のブラウザで試す（Chrome推奨）</li>
                </ol>
              </div>
              <div className="bg-white rounded-lg p-4 border border-red-100">
                <h5 className="font-bold text-gray-800">ログインできない</h5>
                <ol className="text-gray-600 mt-2 text-sm list-decimal list-inside space-y-1">
                  <li>メールアドレスとパスワードが正しいか確認</li>
                  <li>Caps Lockがオンになっていないか確認</li>
                  <li>管理者にパスワードリセットを依頼</li>
                </ol>
              </div>
              <div className="bg-white rounded-lg p-4 border border-red-100">
                <h5 className="font-bold text-gray-800">データが更新されていない</h5>
                <ol className="text-gray-600 mt-2 text-sm list-decimal list-inside space-y-1">
                  <li>自動更新は毎日深夜に実行されます</li>
                  <li>UTAGE連携設定が正しいか確認</li>
                  <li>管理者に手動実行を依頼</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-bold text-blue-800 mb-2">それでも解決しない場合</h4>
            <p className="text-blue-700">
              システム管理者にお問い合わせください。
              その際、以下の情報を伝えるとスムーズに対応できます：
            </p>
            <ul className="mt-2 text-blue-700 text-sm space-y-1">
              <li>・発生した日時</li>
              <li>・どの画面で発生したか</li>
              <li>・どのような操作をしたか</li>
              <li>・エラーメッセージ（表示されていれば）</li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  const filteredSections = sections.filter(
    (section) =>
      section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (typeof section.content === 'object' &&
        JSON.stringify(section.content).toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="戻る"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">使い方ガイド</h1>
                <p className="text-sm text-gray-500">majirakunの機能と使い方を詳しく説明します</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={expandAll}
                className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                すべて開く
              </button>
              <button
                onClick={collapseAll}
                className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                すべて閉じる
              </button>
            </div>
          </div>

          {/* 検索 */}
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="キーワードで検索...（例：ファネル、クリック、CSV）"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-base"
            />
          </div>
        </div>
      </div>

      {/* コンテンツ */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="space-y-2">
          {filteredSections.map((section) => (
            <div
              key={section.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="text-primary">{section.icon}</div>
                  <span className="font-medium text-gray-900 text-left">{section.title}</span>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
              {expandedSections.includes(section.id) && (
                <div className="px-4 pb-6 border-t border-gray-100">
                  <div className="pt-4">{section.content}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredSections.length === 0 && (
          <div className="text-center py-12">
            <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">「{searchQuery}」に一致する項目が見つかりませんでした</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 text-primary hover:underline"
            >
              検索をクリア
            </button>
          </div>
        )}

        {/* フッター */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
          <h3 className="font-bold text-blue-900 text-lg mb-2">お困りの場合は</h3>
          <p className="text-blue-800">
            このガイドで解決しない場合は、<strong>システム管理者</strong>にお問い合わせください。
          </p>
        </div>
      </div>
    </div>
  );
}
